import { SelectionFeature, SelectionGeometry, SelectionType } from "src/js/stores/map/store";
import { useEffect, useMemo, useState } from "react";

import { ConstraintsCategory } from "react-migration/layouts/map/Constraints/types";
import { DemographicsRegion } from "../types";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { attributeComparisonSpecMap } from "../OutputAreaDetailView/OutputAreaDetailView";
import { isDefined } from "react-migration/lib/util/isDefined";
import { reverse } from "ramda";
import { useDesignationById } from "react-migration/domains/constraints/designation/hooks/useDesignationById";
import { useDesignationIntersections } from "../OutputAreaDetailView/useDesignationIntersectionsQuery";
import { useDesignationsByGeometry } from "react-migration/domains/constraints/designation/hooks/useDesignationsByGeometry";

type SiteSelection = SelectionFeature & {
  type: SelectionType.SITE | SelectionType.SHARED_SITE;
};

function isSiteSelection(selection?: SelectionFeature): selection is SiteSelection {
  return !!(selection && (selection.type === SelectionType.SITE || selection.type === SelectionType.SHARED_SITE));
}

type UseDemographicsDataParams = {
  designationId?: string;

  selection?: SelectionFeature;

  geometry?: SelectionGeometry | null;

  region?: DemographicsRegion;
  bufferMeters?: number;
  selectedDesignationSubCategoryId?: ConstraintsCategory;
  selectedDesignationId?: string;
  useIntersections?: boolean;
};

function getTopLevelCategory(regionHierarchy: ConstraintsCategory[]): ConstraintsCategory {
  return regionHierarchy[regionHierarchy.length - 1];
}

/**
 * Comprehensive hook for managing demographics data and designations
 *
 * @param params - Configuration object
 * @param params.designationId - Direct designation ID for OutputAreaDetailView use case
 * @param params.selection - Selection feature for SITE or direct designation selections
 * @param params.geometry - The geometry to search within (for geometry-based queries)
 * @param params.region - The demographics region ('UK' or 'US') - will be auto-detected if not provided
 * @param params.bufferMeters - Buffer distance in meters (default: 0)
 * @param params.selectedDesignationSubCategoryId - Optional category to calculate superior categories from
 * @param params.selectedDesignationId - Designation ID for intersection-based queries
 * @param params.useIntersections - Whether to use designation intersections instead of geometry
 *
 * @returns Object containing all demographics data and hierarchy information
 */
export function useDemographicsData({
  designationId,
  selection,
  geometry,
  region: providedRegion,
  bufferMeters = 0,
  selectedDesignationSubCategoryId,
  selectedDesignationId,
  useIntersections = false,
}: UseDemographicsDataParams) {
  const [targetDesignation, setTargetDesignation] = useState<SingleDesignation | undefined>();
  const [targetDesignationLoading, setTargetDesignationLoading] = useState<boolean>(true);

  const isSite = isSiteSelection(selection);

  const { designation: directDesignation, loading: directDesignationLoading } = useDesignationById(
    designationId || (!isSite ? selection?.id : undefined)
  );

  const allBaseCategoriesWithDetails = useMemo(() => {
    return (
      Object.entries(attributeComparisonSpecMap) as [
        DemographicsRegion,
        (typeof attributeComparisonSpecMap)[DemographicsRegion]
      ][]
    )
      .map(([region, spec]) => ({
        region,
        category: spec.outputAreaCategoryHierarchy[0],
        hierarchy: spec.outputAreaCategoryHierarchy,
        spec,
      }))
      .filter((item) => !!item.category);
  }, []);

  const siteQueryRootCategories = useMemo(
    () => allBaseCategoriesWithDetails.map((item) => item.category).filter(Boolean) as ConstraintsCategory[],
    [allBaseCategoriesWithDetails]
  );

  const { designations: siteIntersectingDesignations, loading: siteIntersectionsLoading } = useDesignationsByGeometry({
    geometry: isSite ? selection.feature?.geometry || null : null,
    bufferMeters: 0,
    rootCategories: siteQueryRootCategories,
    queryOptions: {
      skip: !isSite || !selection.feature?.geometry || siteQueryRootCategories.length === 0,
    },
  });

  useEffect(() => {
    if (!selection) {
      setTargetDesignation(undefined);
      setTargetDesignationLoading(false);
      return;
    }

    if (!isSiteSelection(selection)) {
      setTargetDesignation(directDesignation);
      setTargetDesignationLoading(directDesignationLoading);
    }
  }, [selection, directDesignation, directDesignationLoading]);

  useEffect(() => {
    if (isSiteSelection(selection)) {
      if (siteIntersectionsLoading) {
        setTargetDesignationLoading(true);
        return;
      }
      if (siteIntersectingDesignations && siteIntersectingDesignations.length > 0) {
        let bestMatch: SingleDesignation | undefined = undefined;
        let bestMatchGranularity = Infinity;

        for (const design of siteIntersectingDesignations) {
          for (const { hierarchy } of allBaseCategoriesWithDetails) {
            const indexInHierarchy = hierarchy.indexOf(design.sub_category_id as ConstraintsCategory);
            if (indexInHierarchy !== -1 && indexInHierarchy < bestMatchGranularity) {
              bestMatch = design;
              bestMatchGranularity = indexInHierarchy;
            }
          }
        }
        setTargetDesignation(bestMatch);
      } else {
        setTargetDesignation(undefined);
      }
      setTargetDesignationLoading(false);
    }
  }, [selection, siteIntersectingDesignations, siteIntersectionsLoading, allBaseCategoriesWithDetails]);

  const workingDesignation = useMemo(() => {
    if (designationId) return directDesignation;
    if (selection) return targetDesignation;
    return undefined;
  }, [designationId, directDesignation, selection, targetDesignation]);

  const [detectedRegion, detectedAttributeComparisonSpec] = useMemo(() => {
    const currentDesignation =
      workingDesignation ||
      (selectedDesignationSubCategoryId ? { sub_category_id: selectedDesignationSubCategoryId } : null);
    if (!currentDesignation?.sub_category_id)
      return [providedRegion, providedRegion ? attributeComparisonSpecMap[providedRegion] : undefined];

    const detected = (
      Object.entries(attributeComparisonSpecMap) as [
        DemographicsRegion,
        (typeof attributeComparisonSpecMap)[DemographicsRegion]
      ][]
    ).find(([, spec]) =>
      spec.outputAreaCategoryHierarchy.includes(currentDesignation.sub_category_id as ConstraintsCategory)
    );

    return detected
      ? [detected[0], detected[1]]
      : [providedRegion, providedRegion ? attributeComparisonSpecMap[providedRegion] : undefined];
  }, [workingDesignation, selectedDesignationSubCategoryId, providedRegion]);

  const region = detectedRegion;
  const attributeComparisonSpec = detectedAttributeComparisonSpec;

  const regionHierarchy = useMemo(
    () => attributeComparisonSpec?.outputAreaCategoryHierarchy || [],
    [attributeComparisonSpec]
  );

  const superiorOutputAreaCategories = useMemo(() => {
    const currentDesignation =
      workingDesignation ||
      (selectedDesignationSubCategoryId ? { sub_category_id: selectedDesignationSubCategoryId } : null);
    if (!currentDesignation?.sub_category_id || !attributeComparisonSpec) return [];

    const categoryIndex = attributeComparisonSpec.outputAreaCategoryHierarchy.indexOf(
      currentDesignation.sub_category_id as ConstraintsCategory
    );
    if (categoryIndex === -1) return [];

    return attributeComparisonSpec.outputAreaCategoryHierarchy.slice(categoryIndex + 1);
  }, [workingDesignation, selectedDesignationSubCategoryId, attributeComparisonSpec]);

  const rootCategories = useMemo(() => {
    if (useIntersections || designationId || selection) return superiorOutputAreaCategories;
    if (selectedDesignationSubCategoryId) {
      const selectedIndex = regionHierarchy.indexOf(selectedDesignationSubCategoryId);
      return selectedIndex !== -1 ? regionHierarchy.slice(selectedIndex + 1) : regionHierarchy;
    }
    return regionHierarchy;
  }, [
    useIntersections,
    designationId,
    selection,
    superiorOutputAreaCategories,
    selectedDesignationSubCategoryId,
    regionHierarchy,
  ]);

  const { designations: geometryDesignations = [], loading: geometryLoading } = useDesignationsByGeometry({
    geometry: geometry || null,
    bufferMeters,
    rootCategories: rootCategories as ConstraintsCategory[],
    queryOptions: {
      skip: !!(useIntersections || designationId || selection || !geometry || !attributeComparisonSpec),
    },
  });

  const { designations: intersectionDesignations = [], loading: intersectionLoading } = useDesignationIntersections({
    id: designationId || selectedDesignationId || workingDesignation?.id,
    bufferMeters: useIntersections || designationId || selection ? -5 : bufferMeters,
    rootCategories: useIntersections ? regionHierarchy : superiorOutputAreaCategories,
    skip:
      (!useIntersections && !designationId && !selection) ||
      !(designationId || selectedDesignationId || workingDesignation?.id) ||
      (useIntersections ? regionHierarchy.length === 0 : superiorOutputAreaCategories.length === 0),
  });

  const allDesignations =
    useIntersections || designationId || selection ? intersectionDesignations : geometryDesignations;

  const loading = useMemo(() => {
    if (designationId) return directDesignationLoading || intersectionLoading;
    if (selection) return targetDesignationLoading || intersectionLoading;
    if (useIntersections) return intersectionLoading;
    return geometryLoading;
  }, [
    designationId,
    selection,
    useIntersections,
    directDesignationLoading,
    targetDesignationLoading,
    intersectionLoading,
    geometryLoading,
  ]);

  const { superiorCategories, superiorDesignations } = useMemo(() => {
    if (useIntersections || designationId || selection) {
      if (useIntersections && workingDesignation?.sub_category_id) {
        const currentIndex = regionHierarchy.indexOf(workingDesignation.sub_category_id as ConstraintsCategory);
        if (currentIndex !== -1) {
          const superiorCategories = regionHierarchy.slice(currentIndex + 1);
          const superiorDesignations = allDesignations.filter((d: SingleDesignation) =>
            superiorCategories.includes(d.sub_category_id as ConstraintsCategory)
          );
          return { superiorCategories, superiorDesignations };
        }
      }

      return {
        superiorCategories: superiorOutputAreaCategories,
        superiorDesignations: allDesignations,
      };
    }

    if (!selectedDesignationSubCategoryId) {
      return {
        superiorCategories: regionHierarchy,
        superiorDesignations: allDesignations,
      };
    }

    let bestMatchIndex = Infinity;

    allDesignations.forEach((d: SingleDesignation) => {
      const idx = regionHierarchy.indexOf(d.sub_category_id as ConstraintsCategory);
      if (idx !== -1 && idx < bestMatchIndex) bestMatchIndex = idx;
    });

    const superiorCategories = bestMatchIndex < Infinity ? regionHierarchy.slice(bestMatchIndex + 1) : [];

    const superiorDesignations = allDesignations.filter((d: SingleDesignation) =>
      superiorCategories.includes(d.sub_category_id as ConstraintsCategory)
    );

    return { superiorCategories, superiorDesignations };
  }, [
    useIntersections,
    designationId,
    selection,
    superiorOutputAreaCategories,
    allDesignations,
    selectedDesignationSubCategoryId,
    regionHierarchy,
    workingDesignation,
  ]);

  const comparableDesignations = useMemo(() => {
    if (!attributeComparisonSpec) return [];

    return reverse(attributeComparisonSpec.outputAreaCategoryHierarchy)
      .flatMap((category) => superiorDesignations.filter((oa: SingleDesignation) => oa.sub_category?.id === category))
      .filter(isDefined);
  }, [attributeComparisonSpec, superiorDesignations]);

  const topLevelDesignation = useMemo(() => {
    if (!attributeComparisonSpec) return undefined;

    const topLevelCategory = getTopLevelCategory(attributeComparisonSpec.outputAreaCategoryHierarchy);
    return superiorDesignations.find(
      (designation: SingleDesignation) => designation.sub_category_id === topLevelCategory
    );
  }, [superiorDesignations, attributeComparisonSpec]);

  const defaultDesignationId = useMemo(() => {
    return comparableDesignations[0]?.id;
  }, [comparableDesignations]);

  return {
    targetDesignation: workingDesignation,

    designation: workingDesignation,
    superiorDesignations,
    superiorCategories,
    comparableDesignations,
    topLevelDesignation,
    defaultDesignationId,

    region,
    attributeComparisonSpec,
    regionHierarchy,
    loading,
  };
}
